import { IAction } from "../domain/action.interface";
import { IRoleAction, IUserRole, userService } from "../services/user.service";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MenuItem } from "./menu.store";
import { getIconOrDefault } from "../utils/menuConfig";
import {
	CUPS_ANALYTES_PATH,
	LEGACY_ANALYTES_PATH,
} from "../pages/CupsAndAnalytes/tabs";

/** A retired route is authorised as the route that replaced it (SIMS-8794). */
export const retiredRouteTarget = (path: string): string => {
	const first = `/${(path ?? "").split("/")[1] ?? ""}`.toLowerCase();
	return first === LEGACY_ANALYTES_PATH ? CUPS_ANALYTES_PATH : path;
};

/**
 * SIMS-8717 AC2 — nesting is CONFIGURATION; visibility still follows the GRANT.
 *
 * `granted` is the set of action ids the user's roles actually carry. Children were previously
 * mapped straight from `action.subActions`, which is the parent's configured list — so a user
 * granted a parent saw a dropdown entry for every sub-action configured beneath it, including
 * ones no role had granted them. The entry led to a page they had no permission for, and the
 * navigation was effectively advertising permissions the user did not hold.
 *
 * Filtering here rather than at the call site keeps the rule in one place for the nested levels
 * too: `actionToMenu` recurses, so a grandchild is filtered by the same set.
 */
const actionToMenu = (action: IAction, granted: Set<string>): MenuItem => {
	// `subActions` is populated objects on the read path, but older records return raw id
	// strings (SIMS-6516). Both shapes are matched against the grant set, so a legacy parent
	// keeps the children the user is entitled to instead of silently losing all of them.
	const visibleChildren = action.subActions?.filter((sub) =>
		granted.has(typeof sub === "string" ? sub : sub._id),
	);
	return {
		id: action._id,
		icon: action.icon || getIconOrDefault(action.code),
		title: action.name,
		route: action.link,
		// `parent` vs `link` follows what the user can actually SEE. A parent whose every child
		// is ungrouped from this user's grants is a plain link, not an expander that opens onto
		// nothing.
		type: visibleChildren && visibleChildren.length > 0 ? "parent" : "link",
		open: false,
		active: false,
		children: visibleChildren?.map((sub) => actionToMenu(sub, granted)),
	};
};

/**
 * Derive the sidebar menu items from a user's granted role actions.
 *
 * SIMS-7884: extracted so the login-time fetch (`getUserByEmail`) and the
 * silent mid-session revalidation (`refreshUserInfo`) cannot drift apart — a
 * refresh must rebuild exactly the same menu the login built.
 */
const deriveMenus = (user: IUserRole): MenuItem[] => {
	const allActions = user.roles.flatMap((role) => role.actions);
	// Users granted the B2D dashboard hide the legacy "Dashboard"
	// menu so they only see their entry point.
	const hasB2DDashboard = allActions.some((a) => a.code === "DASH_VIEW_B2D");
	// Users granted the consolidated Reports hide the legacy "Reports"
	// parent so they only see the new entry.
	const hasConsolidatedReports = allActions.some(
		(a) => a.code === "REP_CONS_VIEW",
	);

	// What the user's roles actually GRANT: the flattened role.actions, and nothing else.
	//
	// Deliberately NOT recursive. Walking into each action's `subActions` would fold the parent's
	// CONFIGURED nesting back into the grant set — which is the very thing being filtered against,
	// so every configured child would qualify and the filter would do nothing at all.
	const granted = new Set(allActions.map((a) => a._id));

	return allActions
		.filter((action) => action.type === "MENU_OPTION")
		.filter((action) => !(hasB2DDashboard && action.code === "DASH_VIEW"))
		.filter((action) => !(hasConsolidatedReports && action.code === "REP_VIEW"))
		.sort((a, b) => a.order - b.order)
		.map((action) => actionToMenu(action, granted));
};

// Define the interface for the store's state
export interface UserStore {
	userInfo: IUserRole | null;
	lastFetchedAt: number | null;
	userHasAccess: (actionUrl: string) => boolean;
	userHasAccessToCode: (roleCode: string) => boolean;
	menus: MenuItem[];
	roles: IRoleAction[];
	getUserByEmail: (email: string, subIdp: string) => Promise<IUserRole>;
	refreshUserInfo: (email: string, subIdp: string) => Promise<IUserRole>;
	clearUser: () => void;
	isLoading: boolean;
}

// Create the store
export const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: null,
			lastFetchedAt: null,
			menus: [],
			roles: [],
			isLoading: false,
			userHasAccess: (requested: string) => {
				//everyone has access to unauthorized page or home
				if (["/unauthorized", "/"].includes(requested?.toLowerCase()))
					return true;
				// SIMS-8794 — the retired /analytes route redirects to the merged screen, so it is
				// judged as /cups. Otherwise this guard runs before the redirect and refuses a user
				// whose roles were migrated onto the cups action.
				const path = retiredRouteTarget(requested);

				const user = get().userInfo;
				if (!user) return false;

				return user.roles.some((role) =>
					role.actions.some((a) => {
						const pathIsValid = path.split("/").length > 1;
						const pathSecondPart = path.split("/")[1].toLowerCase();

						const userHasAccess = pathIsValid
							? a.link?.toLowerCase().includes(pathSecondPart)
							: false;
						const userHasChildAccess = a.subActions?.some((subAction) =>
							subAction.link.toLowerCase().includes(pathSecondPart),
						);
						return userHasAccess || userHasChildAccess;
					}),
				);
			},
			userHasAccessToCode: (roleCode: string) => {
				const user = get().userInfo;
				if (!user) return false;

				return user.roles.some((role) =>
					role.actions.some((a) => {
						const userHasAccess = a.code === roleCode;
						const userHasChildAccess = a.subActions?.some(
							(subAction) => subAction.code === roleCode,
						);
						return userHasAccess || userHasChildAccess;
					}),
				);
			},
			getUserByEmail: async (email: string, subIdp: string) => {
				set({ isLoading: true });
				try {
					const user = await userService.findByEmail(email, subIdp);

					set({
						userInfo: user,
						lastFetchedAt: Date.now(),
						menus: deriveMenus(user),
						roles: user.roles,
						isLoading: false,
					});
					return user;
				} catch (error) {
					set({ isLoading: false });
					throw error;
				}
			},
			// SIMS-7884: silent mid-session re-read of the logged-in user.
			//
			// `userInfo` used to be written exactly once — by `getUserByEmail`
			// during App bootstrap — and then persisted to localStorage. Because
			// ~30 screens scope themselves by `userInfo.programs`, a program
			// assigned to the user mid-session stayed invisible everywhere until
			// they signed out and back in (which is what finally cleared the
			// snapshot). This action makes that snapshot refreshable.
			//
			// Deliberately does NOT touch `isLoading`: App.tsx gates the
			// full-screen spinner on it (and Callback reads it too), so flipping
			// it during a background revalidation would blank the app mid-session.
			//
			// On failure the previous snapshot is left untouched and the error is
			// re-thrown for the caller (React Query) to record — a revalidation
			// that cannot reach the backend must never downgrade a working session.
			refreshUserInfo: async (email: string, subIdp: string) => {
				const user = await userService.findByEmail(email, subIdp);

				set({
					userInfo: user,
					lastFetchedAt: Date.now(),
					menus: deriveMenus(user),
					roles: user.roles,
				});
				return user;
			},
			clearUser: () =>
				set({ userInfo: null, lastFetchedAt: null, menus: [], roles: [] }),
		}),
		{
			name: "UserStore",
		},
	),
);
